import { Trash } from "lucide-react";
import { useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Faq1 from "@stackje/shared-components/cards/faq/Faq1";
import Faq2 from "@stackje/shared-components/cards/faq/Faq2";
import { Button } from "@stackje/shared-components/ui/button";
import { Checkbox } from "@stackje/shared-components/ui/checkbox";
import { ClearableInput } from "@stackje/shared-components/ui/clearable-fields";
import { SimpleMarkdownEditor } from "@stackje/shared-components/ui/simple-markdown-editor";
import { Label } from "@stackje/shared-components/ui/label";
import type { CommonColor } from "@stackje/common/config";
import { useColorSchemeShuffler } from "../../../hooks/useColorSchemeShuffler";
import { sampleData } from "@/components/add-card/constants";

// Zod schema for validation
const faqCardFormSchema = z.object({
  title: z.string(),
  description: z.string(),
  expandAll: z.boolean(),
  faqs: z.array(z.object({
    question: z.string().min(1, "Question is required"),
    answer: z.string().min(1, "Answer is required"),
  })).min(1, "At least one FAQ is required"),
  type: z.enum(["faq1", "faq2"]),
  colors: z.object({
    background: z.string(),
    foreground: z.string(),
    cardBackground: z.string(),
    cardForeground: z.string(),
    buttonBackground: z.string(),
  }),
});

export type FaqCardData = z.infer<typeof faqCardFormSchema>;

interface FaqCardFormProps {
  initialData: FaqCardData;
  mode: "add" | "edit";
  onTypeChange?: (newType: string) => void;
}

export function useFaqCardForm({ initialData, mode, onTypeChange }: FaqCardFormProps) {
  const form = useForm<FaqCardData>({
    resolver: zodResolver(faqCardFormSchema),
    defaultValues: initialData,
    mode: "onChange",
  });

  const { append } = useFieldArray({
    control: form.control,
    name: "faqs",
  });

  const { colors, setColors, shuffleColors } = useColorSchemeShuffler(initialData.colors as CommonColor);

  // Watch form values for preview updates
  const watchedValues = form.watch();
  const title = watchedValues.title;
  const description = watchedValues.description;
  const expandAll = watchedValues.expandAll;
  const faqs = watchedValues.faqs;
  const type = watchedValues.type;
  const isDirty = form.formState.isDirty;

  useEffect(() => {
    setColors(initialData.colors as CommonColor);
    form.setValue("colors", initialData.colors);
  }, [initialData.colors, setColors, form]);

  // Update form colors when shuffled
  useEffect(() => {
    form.setValue("colors", colors);
  }, [colors, form]);

  const toggleType = (newType?: string) => {
    if (newType) {
      form.setValue("type", newType as "faq1" | "faq2");
      onTypeChange?.(newType);
      return;
    }

    const types = ["faq1", "faq2"] as const;
    const currentIndex = types.indexOf(type as "faq1" | "faq2");
    const nextIndex = (currentIndex + 1) % types.length;
    form.setValue("type", types[nextIndex]);
    onTypeChange?.(types[nextIndex]);
  };

  const addFaq = () => {
    append({ question: "", answer: "" });
  };



  const formContent = (
    <>
      <div className="space-y-2">
        <div className="py-2">
          <h2 className="text-lg font-semibold">FAQ Card</h2>
          <p className="text-sm text-muted-foreground">
            Not all fields will be used for a given card type.
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="text-red-500">*</span> indicates required fields
          </p>
        </div>

        <div className="border-t pb-2 pt-4">
          <Label htmlFor="title">Title</Label>
          <Controller
            name="title"
            control={form.control}
            render={({ field }) => (
              <ClearableInput
                id="title"
                value={field.value}
                onChange={field.onChange}
                placeholder="Enter title"
              />
            )}
          />
          <div className="my-2">
            <Label htmlFor="description">Description</Label>
            <Controller
              name="description"
              control={form.control}
              render={({ field }) => (
                <SimpleMarkdownEditor
                  id="description"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter description"
                />
              )}
            />
          </div>
        </div>

        <div className="border-t pb-2 pt-4">
          <h3 className="mb-2">FAQs</h3>
          <div className="flex items-center space-x-2 mb-4">
            <Controller
              name="expandAll"
              control={form.control}
              render={({ field }) => (
                <Checkbox
                  id="expandAll"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label htmlFor="expandAll">Expand all FAQs by default</Label>
          </div>

          {faqs.map((_, index) => (
            <div
              key={index}
              className="space-y-2 border py-2 px-4 rounded-md mb-4 relative"
            >
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const updatedFaqs = [...faqs];
                  updatedFaqs.splice(index, 1);
                  form.setValue("faqs", updatedFaqs);
                }}
                aria-label="Delete FAQ"
              >
                <Trash className="h-4 w-4" />
              </Button>
              <div className="mb-2">
                <Label htmlFor={`faqQuestion${index}`}>Question</Label>
                <Controller
                  name={`faqs.${index}.question`}
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <>
                      <ClearableInput
                        id={`faqQuestion${index}`}
                        name={`faqQuestion${index}`}
                        value={field.value || ""}
                        onChange={field.onChange}
                        placeholder="Enter question"
                      />
                      {fieldState.error && (
                        <p className="mt-1 text-sm text-red-600">{fieldState.error.message}</p>
                      )}
                    </>
                  )}
                />
              </div>
              <div className="my-2">
                <Label htmlFor={`faqAnswer${index}`}>Answer</Label>
                <Controller
                  name={`faqs.${index}.answer`}
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <>
                      <SimpleMarkdownEditor
                        id={`faqAnswer${index}`}
                        name={`faqAnswer${index}`}
                        value={field.value || ""}
                        onChange={field.onChange}
                        placeholder="Enter answer"
                        rows={3}
                      />
                      {fieldState.error && (
                        <p className="mt-1 text-sm text-red-600">{fieldState.error.message}</p>
                      )}
                    </>
                  )}
                />
              </div>
            </div>
          ))}

          <Button
            type="button"
            onClick={addFaq}
            variant="outline"
            className="w-full"
          >
            Add Another FAQ
          </Button>
        </div>

        <input type="hidden" name="type" value={type} />
        <input type="hidden" name="expandAll" value={expandAll.toString()} />
        <input type="hidden" name="colors" value={JSON.stringify(colors)} />
      </div>
    </>
  );

  const previewContent = isDirty || mode === "edit" ? (
    type === "faq2" ? (
      <Faq2
        title={title}
        description={description}
        expandAll={expandAll}
        faqs={faqs}
        colors={colors}
      />
    ) : (
      <Faq1
        title={title}
        description={description}
        expandAll={expandAll}
        faqs={faqs}
        colors={colors}
      />
    )
  ) : (
    type === "faq2" ? (
      <Faq2
        title={sampleData.faq2.title}
        description={sampleData.faq2.description}
        expandAll={false}
        faqs={sampleData.faq2.faqs}
        colors={colors}
      />
    ) : (
      <Faq1
        title={sampleData.faq.title}
        description={sampleData.faq.description}
        expandAll={false}
        faqs={sampleData.faq.faqs}
        colors={colors}
      />
    )
  );

  return {
    form,
    formContent,
    previewContent,
    toggleType,
    shuffleColors,
    isDirty,
    handleSubmit: form.handleSubmit,
    formData: {
      title,
      description,
      expandAll,
      faqs,
      type,
      colors,
    } as FaqCardData,
  };
}
